<template>
  <section id="pro-view" class="enterprise-section enterprise-pricing" aria-labelledby="enterprise-pricing-title">
    <div class="enterprise-shell">
      <div class="enterprise-section-heading">
        <h2 id="enterprise-pricing-title">{{ t('enterprise_home_pricing_title') }}</h2>
        <p>{{ pricingDescription }}</p>
      </div>

      <div class="enterprise-segmented" role="tablist" :aria-label="t('enterprise_home_pricing_region_aria')">
        <button type="button" :class="{ active: mode === 'local' }" @click="setMode('local')">
          <EnterpriseIcon :src="mapPinIcon" />{{ localModeLabel }}
        </button>
        <div class="enterprise-segmented__option" :class="{ active: mode === 'global' }">
          <button type="button" class="enterprise-segmented__mode-button" @click="setMode('global')">
            <EnterpriseIcon :src="globeIcon" />{{ globalModeLabel }}
          </button>
          <span
            ref="pricingInfoRef"
            class="enterprise-segmented__info-wrap"
            @mouseenter="schedulePricingInfo"
            @mouseleave="hidePricingInfo"
          >
            <button
              type="button"
              class="enterprise-segmented__info-trigger"
              :class="{ 'is-open': pricingInfoVisible }"
              :aria-label="t('enterprise_home_pricing_info_aria')"
              :aria-expanded="pricingInfoVisible"
              aria-controls="enterprise-pricing-info-tooltip"
              @click.stop.prevent="togglePricingInfo"
              @focus="showPricingInfo"
              @blur="hidePricingInfo"
              @keydown.enter.stop.prevent="togglePricingInfo"
              @keydown.space.stop.prevent="togglePricingInfo"
              @keydown.esc.stop.prevent="closePricingInfo"
            >
              <EnterpriseIcon :src="circleAlertIcon" />
            </button>
            <span
              v-show="pricingInfoVisible"
              id="enterprise-pricing-info-tooltip"
              class="enterprise-pricing-tooltip"
              role="tooltip"
              @click.stop
            >
              {{ pricingInfoText }}
            </span>
          </span>
        </div>
      </div>

      <div class="enterprise-plan-grid" :style="{ '--enterprise-plan-count': plans.length }">
        <article v-for="(plan, index) in plans" :key="plan.name" class="enterprise-plan-card">
          <h3>{{ plan.name }}</h3>
          <p class="enterprise-plan-card__quota">{{ plan.quota }}</p>
          <div class="enterprise-plan-card__price" :class="{ 'is-custom': plan.customized }">
            <strong>{{ plan.price }}</strong><span>{{ plan.period }}</span>
          </div>
          <p class="enterprise-plan-card__seat">
            <EnterpriseIcon :src="usersIcon" />
            {{ plan.features[0] }}
          </p>
          <div class="enterprise-plan-card__divider" />
          <ul>
            <li v-for="(feature, featureIndex) in plan.features.slice(1)" :key="feature" :class="{ 'is-accent': mode === 'global' && featureIndex === 0 }">
              <EnterpriseIcon :src="mode === 'global' && featureIndex === 0 ? arrowLeftRightIcon : layersIcon" />
              {{ feature }}
            </li>
          </ul>
          <button type="button" @click="openPlan(index, Boolean(plan.customized))">{{ plan.action }}</button>
        </article>
      </div>

      <div class="enterprise-global-band">
        <div class="enterprise-global-band__main">
          <div class="enterprise-global-band__content">
            <div class="enterprise-global-band__badges">
              <span><EnterpriseIcon :src="sparklesIcon" />{{ t('enterprise_home_pricing_global_badge') }}</span>
              <b>{{ isVipSite ? 'doclingo.vip' : 'doclingo.ai' }}</b>
              <EnterpriseIcon class="enterprise-global-band__swap" :src="arrowLeftRightIcon" />
              <b>{{ isVipSite ? 'doclingo.ai' : 'doclingo.vip' }}</b>
            </div>
            <div class="enterprise-global-band__title">
              <h3>{{ t('enterprise_home_pricing_global_title') }}</h3>
              <strong>{{ t('enterprise_home_pricing_global_subtitle') }}</strong>
            </div>
            <p>{{ globalBandDescription }}</p>
            <ul>
              <li v-for="item in globalFeatures" :key="item"><EnterpriseIcon :src="circleCheckIcon" />{{ item }}</li>
            </ul>
          </div>

          <aside class="enterprise-global-band__aside">
            <h4>{{ t('enterprise_home_pricing_global_view') }}</h4>
            <div class="enterprise-global-band__toggle-demo">
              <span><EnterpriseIcon :src="mapPinIcon" />{{ localModeLabel }}</span>
              <b><EnterpriseIcon :src="globeIcon" />{{ globalModeLabel }}</b>
              <EnterpriseIcon class="enterprise-global-band__click-hint" :src="mousePointerClickIcon" />
            </div>
            <div class="enterprise-global-band__guide">
              <button type="button" @click="showGlobalPlans"><EnterpriseIcon :src="arrowUpIcon" /><span>{{ t('enterprise_home_pricing_global_guide_plans') }}</span></button>
              <button type="button" @click="showGlobalRights"><EnterpriseIcon :src="arrowDownIcon" /><span>{{ t('enterprise_home_pricing_global_guide_rights') }}</span></button>
            </div>
            <small>{{ counterpartHint }}</small>
          </aside>
        </div>

        <p class="enterprise-global-band__compliance">
          <EnterpriseIcon :src="shieldCheckIcon" />
          {{ t('enterprise_home_pricing_compliance') }}
        </p>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import type ProDialog from '~/components/Pro/ProDialog/ProDialog.vue'
import EnterpriseIcon from '~/components/Index/enterprise/EnterpriseIcon.vue'
import arrowDownIcon from '~/assets/images/enterprise/icons/arrow-down.svg'
import arrowLeftRightIcon from '~/assets/images/enterprise/icons/arrow-left-right.svg'
import arrowUpIcon from '~/assets/images/enterprise/icons/arrow-up.svg'
import circleAlertIcon from '~/assets/images/enterprise/icons/circle-alert.svg'
import circleCheckIcon from '~/assets/images/enterprise/icons/circle-check.svg'
import globeIcon from '~/assets/images/enterprise/icons/globe.svg'
import layersIcon from '~/assets/images/enterprise/icons/layers.svg'
import mapPinIcon from '~/assets/images/enterprise/icons/map-pin.svg'
import mousePointerClickIcon from '~/assets/images/enterprise/icons/mouse-pointer-click.svg'
import shieldCheckIcon from '~/assets/images/enterprise/icons/shield-check.svg'
import sparklesIcon from '~/assets/images/enterprise/icons/sparkles.svg'
import usersIcon from '~/assets/images/enterprise/icons/users.svg'
import { getEnterpriseCurrencySymbol, getEnterprisePlans, type EnterpriseCatalogData, type EnterprisePricingMode } from '~/config/enterpriseLanding'
import { useSiteConfig } from '~/config/useSiteConfig'
import { getProBusinessSubscribeConfig } from '~/config/proConfig'
import useProStore from '~/store/modules/pro'
import useUserStore from '~/store/modules/user'

const props = defineProps<{ mode: EnterprisePricingMode }>()
const emit = defineEmits<{
  'update:mode': [mode: EnterprisePricingMode]
}>()
const { siteEdition, isVipSite } = useSiteConfig()
const { locale, t } = useI18n()
const proStore = useProStore()
const userStore = useUserStore()
const { proConfig, globalProConfig, isEnterpriseCatalogReady } = storeToRefs(proStore)
const { roleRightsMap } = storeToRefs(userStore)
const proDialogRef = inject('proDialogRef') as Ref<InstanceType<typeof ProDialog>>
const currentCurrency = computed(() => userStore.getCurrentCoin(locale.value))
const currentCurrencySymbol = computed(() => getEnterpriseCurrencySymbol(currentCurrency.value))
const catalog = computed<EnterpriseCatalogData>(() => ({
  currency: currentCurrency.value,
  localPrices: isEnterpriseCatalogReady.value ? proConfig.value : [],
  globalPrices: isEnterpriseCatalogReady.value ? globalProConfig.value : [],
  rights: roleRightsMap.value,
}))
const plans = computed(() => getEnterprisePlans(siteEdition.value, props.mode, catalog.value, t, locale.value))
const localModeLabel = computed(() => t(isVipSite.value ? 'enterprise_home_mode_cn' : 'enterprise_home_mode_ai', { currency: currentCurrencySymbol.value }))
const globalModeLabel = computed(() => t(isVipSite.value ? 'enterprise_home_mode_global_collaboration' : 'enterprise_home_mode_global_with_china'))
const pricingDescription = computed(() => {
  if (props.mode === 'global') {
    return t(isVipSite.value ? 'enterprise_home_pricing_description_global_cn' : 'enterprise_home_pricing_description_global_ai')
  }
  return t(isVipSite.value ? 'enterprise_home_pricing_description_local_cn' : 'enterprise_home_pricing_description_local_ai')
})
const globalBandDescription = computed(() => isVipSite.value
  ? t('enterprise_home_pricing_global_description_cn')
  : t('enterprise_home_pricing_global_description_ai'))
const globalFeatures = computed(() => [
  t(isVipSite.value ? 'enterprise_home_pricing_global_feature_members_cn' : 'enterprise_home_pricing_global_feature_members_ai'),
  t('enterprise_home_pricing_global_feature_quota'),
  t('enterprise_home_pricing_global_feature_switch'),
  t('enterprise_home_pricing_global_feature_storage'),
  t(isVipSite.value ? 'enterprise_home_pricing_global_feature_invoice_cn' : 'enterprise_home_pricing_global_feature_invoice_ai'),
])
const counterpartHint = computed(() => isVipSite.value
  ? t('enterprise_home_pricing_counterpart_ai')
  : t('enterprise_home_pricing_counterpart_cn'))
const pricingInfoText = computed(() => isVipSite.value
  ? t('enterprise_home_pricing_info_cn')
  : t('enterprise_home_pricing_info_ai'))
const pricingInfoRef = ref<HTMLElement | null>(null)
const pricingInfoVisible = ref(false)
const pricingInfoPinned = ref(false)
let pricingInfoTimer: ReturnType<typeof setTimeout> | undefined

function clearPricingInfoTimer() {
  if (pricingInfoTimer) {
    clearTimeout(pricingInfoTimer)
    pricingInfoTimer = undefined
  }
}

function schedulePricingInfo() {
  clearPricingInfoTimer()
  pricingInfoTimer = setTimeout(() => {
    pricingInfoVisible.value = true
    pricingInfoTimer = undefined
  }, 300)
}

function showPricingInfo() {
  clearPricingInfoTimer()
  pricingInfoVisible.value = true
}

function hidePricingInfo() {
  clearPricingInfoTimer()
  if (!pricingInfoPinned.value) pricingInfoVisible.value = false
}

function togglePricingInfo() {
  clearPricingInfoTimer()
  pricingInfoPinned.value = !pricingInfoPinned.value
  pricingInfoVisible.value = pricingInfoPinned.value
}

function closePricingInfo() {
  clearPricingInfoTimer()
  pricingInfoPinned.value = false
  pricingInfoVisible.value = false
}

function handlePricingInfoOutsideClick(event: MouseEvent) {
  if (pricingInfoPinned.value && !pricingInfoRef.value?.contains(event.target as Node)) closePricingInfo()
}

onMounted(() => document.addEventListener('click', handlePricingInfoOutsideClick))
onBeforeUnmount(() => {
  clearPricingInfoTimer()
  document.removeEventListener('click', handlePricingInfoOutsideClick)
})

function setMode(mode: EnterprisePricingMode) {
  emit('update:mode', mode)
}

function openSupport() {
  window.$crisp?.do?.('chat:open')
}

function openPlan(index: number, customized: boolean) {
  if (customized) {
    openSupport()
    return
  }

  const backendPlan = getProBusinessSubscribeConfig(currentCurrency.value, props.mode)[index]
  if (!backendPlan) {
    openSupport()
    return
  }
  proDialogRef.value?.open({ ...backendPlan, mode: 0, pricingMode: props.mode }, currentCurrency.value)
}

function showGlobalPlans() {
  setMode('global')
  nextTick(() => document.getElementById('pro-view')?.scrollIntoView({ behavior: 'smooth', block: 'start' }))
}

function showGlobalRights() {
  setMode('global')
  nextTick(() => document.getElementById('rights-view')?.scrollIntoView({ behavior: 'smooth', block: 'start' }))
}

</script>
